import { useState } from 'react';
import { Helmet } from '../components/Helmet';
import { motion } from 'framer-motion';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  Clock,
  Users,
  BarChart,
  CheckCircle,
  ListTree,
  ShieldCheck,
  Award,
  PhoneCall,
  Star,
} from 'lucide-react';
import { getCourseBySlug, getRelatedCourses } from '../data/courses';
import CallbackRequestModal from '../components/ui/CallbackRequestModal';

const BASE_URL = 'https://www.upnov.com';

const clientLogos = [
  'edf',
  'ratp',
  'orano',
  'loreal',
  'vinci',
  'eiffage',
  'laposte',
  'yvesRocher',
  'sncf',
  'carrefour',
];

const detailTestimonials = [
  {
    id: 1,
    name: 'Sophie D.',
    position: "Directrice de l'Innovation, Groupe de Luxe",
    image: '/testi-sophie.webp',
    quote:
      "Grâce aux formations d'UPNov IA, notre équipe a pu mettre en place une stratégie IA cohérente qui a transformé notre approche business. Le ROI a été immédiat !",
    rating: 5,
  },
  {
    id: 2,
    name: 'Marc L.',
    position: 'DSI, Entreprise de Services Numériques',
    image: '/testi-marc.webp',
    quote:
      "La qualité des formateurs et l'approche pratique nous ont permis d'implémenter rapidement des solutions d'IA performantes. Nos équipes sont désormais autonomes.",
    rating: 5,
  },
  {
    id: 3,
    name: 'Nathalie P.',
    position: 'DRH, Groupe Industriel',
    image: '/testi-nathalie.webp',
    quote:
      "La formation sur mesure a parfaitement répondu à nos attentes. L'accent mis sur l'éthique et la responsabilité dans l'usage de l'IA nous a particulièrement séduits.",
    rating: 5,
  },
];

const renderStars = (rating: number) =>
  Array.from({ length: 5 }).map((_, i) => (
    <Star
      key={i}
      className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
    />
  ));

const pedagogie = [
  {
    title: 'Mises en situation',
    text: 'Exercices basés sur vos propres dossiers et outils du quotidien',
  },
  {
    title: 'Cas d’usage sectoriels',
    text: 'Exemples et démonstrations adaptés à votre secteur d’activité',
  },
  {
    title: 'Illustrations concrètes',
    text: 'Démonstrations live sur les principaux outils IA du marché',
  },
  {
    title: 'Évaluation des acquis',
    text: 'Quizz de validation et plan d’action individuel en fin de session',
  },
];

const toc = [
  { id: 'description', label: 'Description' },
  { id: 'public', label: 'Pour qui ?' },
  { id: 'objectifs', label: 'Objectifs' },
  { id: 'programme', label: 'Ce que vous allez découvrir' },
  { id: 'pedagogie', label: 'Pédagogie' },
  { id: 'financement', label: 'Financement' },
  { id: 'faq', label: 'FAQ' },
];

const FormationDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [openProgramme, setOpenProgramme] = useState<number | null>(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  if (!slug) {
    return <Navigate to="/formation-ia-entreprise/" replace />;
  }

  const course = getCourseBySlug(slug);

  if (!course) {
    return <Navigate to="/formation-ia-entreprise/" replace />;
  }

  const relatedCourses = getRelatedCourses(course, 2);
  const canonicalUrl = `${BASE_URL}/formationIA/${course.slug}/`;
  const ogImage = course.image.startsWith('http') ? course.image : `${BASE_URL}${course.image}`;
  const metaDescription = course.description.length > 160
    ? `${course.description.slice(0, 157)}...`
    : course.description;

  return (
    <>
      <Helmet>
        <title>{course.title} | Upnov IA</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={course.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      {/* Hero */}
      <section className="bg-primary text-white py-16 md:py-24">
        <div className="container-custom">
          <nav className="text-sm text-white/60 flex items-center gap-2 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <span>/</span>
            <Link to="/formation-ia-entreprise/" className="hover:text-white transition-colors">Formations</Link>
            <span>/</span>
            <span className="text-white/90 truncate max-w-[200px] sm:max-w-none">{course.title}</span>
          </nav>

          <span className="inline-block bg-white/15 text-white text-xs font-semibold uppercase tracking-wide py-1.5 px-4 rounded-full mb-5">
            {course.category}
          </span>

          <h1 className="heading-lg mb-6 max-w-3xl">{course.title}</h1>
          <p className="text-xl text-white/80 mb-8 max-w-2xl">{course.description}</p>

          <div className="flex flex-wrap gap-6 text-white/85 text-sm mb-4">
            <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{course.duration}</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4" />{course.participants}</span>
            <span className="flex items-center gap-2"><BarChart className="h-4 w-4" />{course.level}</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Présentiel ou distanciel</span>
          </div>

          {course.durationOptions.filter(option => option !== course.duration).length > 0 && (
            <p className="text-xs text-white/60 mb-8">
              {course.durationOptions.filter(option => option !== course.duration).length > 1 ? 'Autres formats : ' : 'Autre format : '}
              {course.durationOptions.filter(option => option !== course.duration).join(', ')}
            </p>
          )}

          <div className="flex flex-wrap gap-4">
            <Link to={`/DemandeInfo/?id=${course.id}`} className="btn-accent">
              Demander un devis
            </Link>
            <a href="#programme" className="bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 border border-white/20">
              Voir le programme
            </a>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <div className="container-custom">
        <div className="-mt-10 relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: '1 300+', label: 'professionnels formés' },
            { value: '4.8/5', label: 'satisfaction moyenne' },
            { value: '80+', label: 'entreprises accompagnées' },
            { value: '15 ans', label: 'd’expertise data & IA' },
          ].map((stat) => (
            <div key={stat.label} className="card p-5 text-center">
              <p className="text-2xl font-bold text-primary mb-1">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-10 items-start">
            <div>
              {/* Description */}
              <motion.div
                id="description"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card p-8 md:p-10 mb-6 scroll-mt-24"
              >
                <h2 className="heading-md mb-4">Description</h2>
                <p className="text-gray-600 leading-relaxed">{course.description}</p>
              </motion.div>

              {/* Pour qui */}
              {course.publicCible && course.publicCible.length > 0 && (
                <motion.div
                  id="public"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="card p-8 md:p-10 mb-6 scroll-mt-24"
                >
                  <h2 className="heading-md mb-6">Pour qui ?</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {course.publicCible.map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Objectifs */}
              <motion.div
                id="objectifs"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-primary/5 rounded-xl p-8 md:p-10 mb-6 scroll-mt-24"
              >
                <h2 className="heading-md mb-6">Objectifs pédagogiques</h2>
                <div className="space-y-4">
                  {course.benefits.map((benefit, i) => (
                    <div key={benefit} className="flex items-start gap-4">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-gray-700 pt-0.5">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Programme */}
              {course.programme && course.programme.length > 0 && (
                <>
                  <motion.div
                    id="programme"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="card p-8 md:p-10 mb-6 scroll-mt-24"
                  >
                    <h2 className="heading-md mb-6 flex items-center gap-3">
                      <ListTree className="h-6 w-6 text-primary" />
                      Ce que vous allez découvrir
                    </h2>
                    <div className="space-y-4">
                      {course.programme.map((block, idx) => (
                        <div key={block.title} className="border border-gray-100 rounded-xl overflow-hidden">
                          <button
                            onClick={() => setOpenProgramme(openProgramme === idx ? null : idx)}
                            className="w-full bg-gray-50 py-3 px-5 flex items-center justify-between gap-4 text-left hover:bg-gray-100 transition-colors"
                            aria-expanded={openProgramme === idx}
                          >
                            <span className="font-semibold text-gray-900">{block.title}</span>
                            <span className="flex items-center gap-3 shrink-0">
                              <span className="text-sm text-primary font-medium">{block.duration}</span>
                              <ChevronDown
                                className={`h-4 w-4 text-primary transition-transform duration-300 ${openProgramme === idx ? 'rotate-180' : ''}`}
                                aria-hidden="true"
                              />
                            </span>
                          </button>

                          {openProgramme === idx && (
                            <motion.ul
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              transition={{ duration: 0.3 }}
                              className="p-5 space-y-3"
                            >
                              {block.items.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm text-gray-600">
                                  <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs italic text-gray-400 mt-6">
                      Programme indicatif, adapté et finalisé en fonction de vos enjeux, de votre secteur d'activité et du niveau des participants.
                    </p>
                  </motion.div>

                  {/* Mini CTA après le programme */}
                  <div className="bg-primary/5 border border-primary/10 rounded-xl p-6 md:p-8 mb-6 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Un programme adapté à vos besoins</p>
                      <p className="text-sm text-gray-600">Chaque parcours est ajusté à votre secteur et au niveau de vos équipes.</p>
                    </div>
                    <Link to={`/DemandeInfo/?id=${course.id}`} className="btn-primary shrink-0">
                      Recevoir le programme détaillé
                    </Link>
                  </div>
                </>
              )}

              {/* Pédagogie */}
              <motion.div
                id="pedagogie"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card p-8 md:p-10 mb-6 scroll-mt-24"
              >
                <h2 className="heading-md mb-6">Méthode pédagogique</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {pedagogie.map((item) => (
                    <div key={item.title} className="bg-gray-50 rounded-xl p-5">
                      <Award className="h-5 w-5 text-primary mb-3" />
                      <p className="font-semibold text-gray-900 mb-1">{item.title}</p>
                      <p className="text-sm text-gray-500">{item.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Ils nous font confiance */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card p-8 md:p-10 mb-6"
              >
                <h2 className="heading-md mb-6">Ils nous font confiance</h2>
                <div className="flex flex-wrap items-center gap-8 opacity-80 mb-10">
                  {clientLogos.map((logo) => (
                    <img
                      key={logo}
                      src={`/logo/logo-${logo}.webp`}
                      alt={`${logo} logo`}
                      width="120"
                      height="48"
                      className="h-10 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                    />
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {detailTestimonials.map((testimonial, index) => (
                    <motion.div
                      key={testimonial.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-gray-50 p-6 rounded-xl"
                    >
                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          width="48"
                          height="48"
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-medium text-sm">{testimonial.name}</h3>
                          <p className="text-xs text-gray-500">{testimonial.position}</p>
                        </div>
                      </div>

                      <div className="flex mb-3">{renderStars(testimonial.rating)}</div>

                      <blockquote className="italic text-sm text-gray-600">
                        "{testimonial.quote}"
                      </blockquote>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Financement */}
              <motion.div
                id="financement"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card p-8 md:p-10 mb-6 scroll-mt-24"
              >
                <h2 className="heading-md mb-4">Tarifs & financement</h2>
                <div className="flex flex-wrap items-center justify-between gap-6 bg-gray-50 rounded-xl p-6">
                  <div>
                    <p className="text-2xl font-bold text-gray-900 mb-1">
                      {course.price ? course.price : 'Sur devis, adapté à vos besoins'}
                    </p>
                    <p className="text-sm text-gray-500">
                      Formation éligible à une prise en charge OPCO. Devis personnalisé sous 24H selon effectif et format.
                    </p>
                  </div>
                  <ShieldCheck className="h-10 w-10 text-primary shrink-0" />
                </div>
              </motion.div>

              {/* FAQ — boosts SEO/GEO citability */}
              {course.faq && course.faq.length > 0 && (
                <motion.div
                  id="faq"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="card p-8 md:p-10 mb-6 scroll-mt-24"
                >
                  <h2 className="heading-md mb-6">Questions fréquentes</h2>
                  <div className="space-y-4">
                    {course.faq.map((item, idx) => (
                      <div key={item.question} className="border border-gray-100 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                          className="w-full bg-gray-50 py-4 px-5 text-left flex items-start justify-between gap-4 hover:bg-gray-100 transition-colors"
                          aria-expanded={openFaq === idx}
                        >
                          <span className="font-semibold text-gray-900">{item.question}</span>
                          <ChevronDown
                            className={`h-5 w-5 text-primary shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`}
                            aria-hidden="true"
                          />
                        </button>

                        {openFaq === idx && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.3 }}
                            className="px-5 py-4 text-sm text-gray-600 leading-relaxed"
                          >
                            {item.answer}
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Related courses */}
              {relatedCourses.length > 0 && (
                <div className="mb-6">
                  <h2 className="heading-md mb-6">Autres formations</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {relatedCourses.map((related) => (
                      <Link
                        key={related.id}
                        to={`/formationIA/${related.slug}/`}
                        className="card p-6 block hover:-translate-y-1 transition-transform duration-300"
                      >
                        <span className="inline-block bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide py-1.5 px-3 rounded-full mb-3">
                          {related.duration}
                        </span>
                        <p className="font-semibold text-gray-900 mb-2 leading-snug">{related.title}</p>
                        <p className="text-sm text-gray-500 line-clamp-2">{related.description}</p>
                      </Link>
                    ))}
                  </div>
                  <div className="text-center mt-8">
                    <Link to="/formation-ia-entreprise/" className="btn-secondary">
                      Voir le catalogue des formations
                    </Link>
                  </div>
                </div>
              )}

              {/* CTA final */}
              <div className="bg-primary rounded-xl p-10 md:p-12 text-center text-white">
                <h3 className="heading-md mb-4">Parlons de votre projet de formation</h3>
                <p className="text-white/80 mb-8 max-w-xl mx-auto">
                  Chaque formation est adaptée à votre entreprise, votre secteur et le niveau de vos équipes. Échangeons quelques minutes pour définir la meilleure formule et recevoir une proposition personnalisée.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <button onClick={() => setCallbackOpen(true)} className="btn-accent">
                    <PhoneCall className="mr-2 h-4 w-4" />
                    Être rappelé
                  </button>
                  <Link
                    to={`/DemandeInfo/?id=${course.id}`}
                    className="text-white font-medium underline underline-offset-4 hover:text-white/80 transition-colors"
                  >
                    Demander un devis
                  </Link>
                </div>
              </div>

              <CallbackRequestModal
                isOpen={callbackOpen}
                onClose={() => setCallbackOpen(false)}
                courseTitle={course.title}
              />
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block sticky top-24 space-y-6">
              <div className="card p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                  <ListTree className="h-4 w-4 text-primary" />
                  Sommaire
                </p>
                <div className="flex flex-col gap-1">
                  {toc.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="text-sm text-gray-500 hover:text-primary py-1.5 pl-3 border-l-2 border-transparent hover:border-primary transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-primary/5 rounded-xl p-6">
                <p className="font-semibold text-gray-900 mb-2">Devis sous 24H</p>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Programme personnalisé selon votre secteur et vos objectifs.
                </p>
                <Link
                  to={`/DemandeInfo/?id=${course.id}`}
                  className="btn-primary w-full text-sm"
                >
                  Demander un devis
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
};

export default FormationDetailPage;
